import React, { useEffect, useState } from 'react'

import { Drawer } from 'scripts/react-components/shared/components'

import * as DS from './exchangeDrawer.styled'

type View = 'exchange' | 'help'

type RewardTier = {
  activationCode: string
  unitPoints: number
  unitValueBRL: number
  availabilityFactor: number
}

type ExpandedOption = {
  activationCode: string
  units: number
  points: number
  valueBRL: number
  unitPoints: number
  unitValueBRL: number
  availabilityFactor: number
}

type Props = {
  open: boolean
  onClose: () => void
  defaultView?: View
  clientNumber: number
  getToken: () => string | null
  onConfirmExchange?: (payload: {
    activationCode: string
    valueBRL: number
    points: number
    units: number
  }) => void
  availablePoints?: number
}

type Faq = { q: string; a: string }

type LoyaltyRewardApi = {
  loyaltyReward?: {
    counterRequirement?: {
      amount?: number | string
    }
    triggerCode?: string
    value?: number | string
  }
  availabilityFactor?: number | string
}

type LoyaltyRewardsResponse = {
  data?: {
    clientLoyaltyRewards?: LoyaltyRewardApi[]
  }
  clientLoyaltyRewards?: LoyaltyRewardApi[]
}

const FAQS: Faq[] = [
  {
    q: 'Preciso trocar meus pontos para utilizar um cashback?',
    a: 'Sim. Você precisa trocar seus pontos para utilizar um cashback. Caso seja feito pelo site/app você poderá escolher o valor desejado, ou caso seja feito pelo PDV ou Self, informar ao caixa o valor que deseja utilizar, e efetuar o resgate digitando um código de segurança recebido via whatsapp.'
  },
  {
    q: 'Como usar o cashback?',
    a: `No site/app:  Na etapa do pagamento selecione em “Benefícios disponíveis” o voucher desejado. Ao aplicar o cashback, o desconto aparecerá no subtotal.\n
      Na loja: Identifique a compra com o seu CPF e informe ao caixa o valor que deseja utilizar. Ou faça o resgate através do PDV/Self checkout.
    `
  },
  {
    q: 'Regras de utilização do cashback:',
    a: `O cashback é intransferível e válido apenas para o CPF que o resgatou.\n
      Válido apenas para produtos vendidos e entregues pela LEROY MERLIN.\n
      O desconto não é aplicado para os valores de frete.\n
      O Cashback é acumulativo com outros descontos.\n
      O cashback pode ser usado de forma integral ou parcial, sendo que na forma parcial será gerado um “troco” que poderá ser utilizado em uma próxima compra.\n
      O troco gerado deve ser utilizado dentro da mesma validade do cashback, que estará descrito no voucher.
    `
  },
  {
    q: 'Qual é o prazo para utilização do cashback?',
    a: 'A partir do momento da troca do ponto, o cashback fica disponível para utilização até o último dia do mês subsequente ao resgate. A data de vencimento é informada no próprio cashback.'
  },
  {
    q: 'Posso utilizar mais de um cashback por compra?',
    a: 'Não. Você só poderá escolher um valor para utilizar na compra. Seja o valor total ou parcial. Caso seja gerado um troco, você poderá utilizar na próxima compra, até a data de validade descrita no cashback.'
  },
  {
    q: 'Como faço para acompanhar os cashbacks que eu já ativei?',
    a: 'Na home do fidelidade, você consegue acompanhar a quantidade de pontos disponíveis, e caso tenha feito a troca de pontos, os cashbacks aparecerão ao lado com uma cor roxa. Isso significa que esses são os cashbacks que estão disponíveis para utilização. É só selecionar no momento da compra.'
  },
  {
    q: 'Posso usar meu cashback na compra de serviços e em produtos Mktplace?',
    a: 'Não. O cashback só poderá ser utilizado em produtos que sejam vendidos e entregues pela Leroy Merlin.'
  },
  {
    q: 'Meus pontos expiram?',
    a: 'Sim. Porém, se forem realizadas compras no período de expiração, os pontos são renovados por mais 03 meses. Os pontos serão expirados, quando não tiver compras ou entrada de pontos no período de 03 meses.'
  }
]

export const ExchangeDrawer: React.FC<Props> = ({
  open,
  onClose,
  defaultView = 'exchange',
  onConfirmExchange,
  availablePoints,
  clientNumber,
  getToken
}) => {
  const [view, setView] = useState<View>('exchange')
  const [openFaq, setOpenFaq] = useState<Set<number>>(new Set())

  const [options, setOptions] = useState<ExpandedOption[]>([])
  const [isLoadingOptions, setIsLoadingOptions] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)

  const [selectedIdx, setSelectedIdx] = useState(0)

  useEffect(() => {
    if (!open) {
      return
    }

    setView(defaultView)
    setOpenFaq(new Set())
    setLoadError(null)

    const fetchOptions = async () => {
      setIsLoadingOptions(true)

      try {
        const token = getToken()

        if (!token) {
          setLoadError('Token não encontrado. Faça login novamente.')
          setOptions([])
          return
        }

        const res = await fetch(
          `/api/v2/loyaltyProgram/lys/cotation/clientLoyaltyRewards?clientNumber=${clientNumber}&nextRewardPrediction=ALL`,
          {
            credentials: 'include',
            headers: {
              accept: 'application/json, text/plain, */*',
              Authorization: `Bearer ${token}`
            }
          }
        )

        if (!res.ok) {
          setLoadError(`Erro ao buscar opções de troca (${res.status}).`)
          setOptions([])
          return
        }

        const json: LoyaltyRewardsResponse = await res.json()

        const root = json?.data ?? json ?? {}
        const list = Array.isArray(root?.clientLoyaltyRewards)
          ? root.clientLoyaltyRewards
          : []

        const tiers: RewardTier[] = list
          .map((item: LoyaltyRewardApi) => {
            const lr = item?.loyaltyReward

            const unitPoints = Number(lr?.counterRequirement?.amount ?? 0)
            const activationCode = String(lr?.triggerCode ?? '')
            const unitValueBRL = Number(lr?.value ?? 0)
            const availabilityFactor = Number(item?.availabilityFactor ?? 0)

            return {
              activationCode,
              unitPoints,
              unitValueBRL,
              availabilityFactor
            }
          })
          .filter(
            t =>
              t.activationCode &&
              t.unitPoints > 0 &&
              t.unitValueBRL > 0 &&
              t.availabilityFactor > 0
          )

        const expanded: ExpandedOption[] = tiers
          .flatMap(tier => {
            const maxByFactor = tier.availabilityFactor

            const maxByPoints =
              typeof availablePoints === 'number' && availablePoints >= 0
                ? Math.floor(availablePoints / tier.unitPoints)
                : maxByFactor

            const maxUnits = Math.max(0, Math.min(maxByFactor, maxByPoints))

            const arr: ExpandedOption[] = []

            for (let units = 1; units <= maxUnits; units++) {
              arr.push({
                activationCode: tier.activationCode,
                units,
                points: units * tier.unitPoints,
                valueBRL: units * tier.unitValueBRL,
                unitPoints: tier.unitPoints,
                unitValueBRL: tier.unitValueBRL,
                availabilityFactor: tier.availabilityFactor
              })
            }

            return arr
          })
          .sort((a, b) => a.points - b.points)

        setOptions(expanded)
        setSelectedIdx(Math.max(0, expanded.length - 1))

        if (expanded.length === 0) {
          setLoadError('Nenhuma opção de troca disponível para seus pontos.')
        }
      } catch {
        setLoadError('Erro inesperado ao buscar opções de troca.')
        setOptions([])
      } finally {
        setIsLoadingOptions(false)
      }
    }

    fetchOptions()
  }, [open, defaultView, clientNumber, availablePoints, getToken])

  useEffect(() => {
    if (!open) return

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }

    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  useEffect(() => {
    if (!open) return
    setSelectedIdx(prev => Math.min(prev, Math.max(0, options.length - 1)))
  }, [options.length, open])

  const toggleFaq = (idx: number) => {
    setOpenFaq(prev => {
      const next = new Set(prev)
      next.has(idx) ? next.delete(idx) : next.add(idx)
      return next
    })
  }

  const selected = options[selectedIdx]
  const minOption = options[0]
  const maxOption = options[options.length - 1]

  const valueBRLCentered = Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(selected?.valueBRL ?? 0)

  const percent =
    options.length <= 1
      ? 0
      : Math.round((selectedIdx / (options.length - 1)) * 100)

  const cssVars = { ['--p' as string]: `${percent}%` } as React.CSSProperties
  const edge: 'left' | 'middle' | 'right' =
    percent < 8 ? 'left' : percent > 92 ? 'right' : 'middle'

  const confirmClick = () => {
    if (!selected) return

    onConfirmExchange?.({
      activationCode: selected.activationCode,
      valueBRL: selected.valueBRL,
      points: selected.points,
      units: selected.units
    })
  }

  const canShowRange = !isLoadingOptions && options.length > 0
  const hasNoOptions = !isLoadingOptions && options.length === 0 && !loadError

  return (
    <Drawer isOpen={open} onClose={onClose} title="" dataCy="exchange-drawer">
      <div
        style={{
          width: '100%',
          boxSizing: 'border-box',
          padding: '8px 4px 24px'
        }}>
        {view === 'exchange' ? (
          <>
            <DS.Title>Quantos pontos deseja trocar?</DS.Title>

            <DS.Subtitle>
              Arraste a barra para escolher quantos pontos deseja trocar por
              cashback.
            </DS.Subtitle>

            {isLoadingOptions && (
              <div style={{ marginTop: 24 }}>
                <DS.SkeletonLine />
                <DS.SkeletonRange />
                <DS.SkeletonLine small />
              </div>
            )}

            {!isLoadingOptions && loadError && (
              <div style={{ marginTop: 12, fontSize: 14 }}>{loadError}</div>
            )}

            {!isLoadingOptions && hasNoOptions && (
              <div style={{ marginTop: 12, fontSize: 14 }}>
                Nenhuma opção de troca disponível no momento.
              </div>
            )}

            {canShowRange && (
              <>
                <DS.RangeRow>
                  <span>
                    R$ <strong>{minOption?.valueBRL ?? 0}</strong>
                  </span>
                  <span>
                    R$ <strong>{maxOption?.valueBRL ?? 0}</strong>
                  </span>
                </DS.RangeRow>

                <DS.RangeWrapper style={cssVars}>
                  <DS.CenterValue aria-hidden="true">
                    <span>{valueBRLCentered.replace('R$', '').trim()}</span>
                  </DS.CenterValue>

                  <DS.Range
                    type="range"
                    min={0}
                    max={Math.max(0, options.length - 1)}
                    step={1}
                    value={selectedIdx}
                    onChange={e => setSelectedIdx(parseInt(e.target.value, 10))}
                  />

                  <DS.PointsBubble data-edge={edge}>
                    {Intl.NumberFormat('pt-BR').format(selected?.points ?? 0)}{' '}
                    <span>pts</span>
                  </DS.PointsBubble>
                </DS.RangeWrapper>

                <DS.PointsRow>
                  <span>
                    <strong>
                      {Intl.NumberFormat('pt-BR').format(
                        minOption?.points ?? 0
                      )}
                    </strong>{' '}
                    pts
                  </span>
                  <span>
                    <strong>
                      {Intl.NumberFormat('pt-BR').format(
                        maxOption?.points ?? 0
                      )}
                    </strong>{' '}
                    pts
                  </span>
                </DS.PointsRow>
              </>
            )}

            <DS.PrimaryButton
              type="button"
              onClick={confirmClick}
              disabled={isLoadingOptions || options.length === 0}>
              Trocar por Cashback
            </DS.PrimaryButton>

            <DS.HelpRow>
              <button type="button" onClick={() => setView('help')}>
                Como usar meu cashback
                <DS.ChevronDown viewBox="0 0 24 24">
                  <path
                    d="M6 9l6 6 6-6"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                </DS.ChevronDown>
              </button>
            </DS.HelpRow>
          </>
        ) : (
          <>
            <DS.Title>Como usar</DS.Title>

            <DS.FaqList>
              {FAQS.map((item, idx) => {
                const isOpen = openFaq.has(idx)

                return (
                  <DS.FaqItem key={idx} data-open={isOpen}>
                    <DS.FaqQuestion onClick={() => toggleFaq(idx)}>
                      <span>{item.q}</span>
                      <DS.FaqChevron data-open={isOpen} viewBox="0 0 24 24">
                        <path
                          d="M6 9l6 6 6-6"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        />
                      </DS.FaqChevron>
                    </DS.FaqQuestion>

                    <DS.FaqAnswer aria-hidden={!isOpen}>{item.a}</DS.FaqAnswer>
                  </DS.FaqItem>
                )
              })}
            </DS.FaqList>
          </>
        )}
      </div>
    </Drawer>
  )
}
